import type {DiffLine} from '@github-ui/diff-lines'
import {useDiffSummaries, useThreads} from '../stores/selectors'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import type {DiffEntry, DiffSide} from '@github-ui/diff-lines/types'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {Thread, StaticDiffLine} from '@github-ui/diff-lines/conversation-types'
import {useMemo} from 'react'
import type {CommentPreviews} from '../components/toolbar/PreviewAuthors'
import type {PendingCommentPreview} from '../stores/slices/pending-review'

const defaultContextLinesToGrab = 5
// While looping through the diff entries to find thread positions twice is not recommended, we do it here so that all
// of the diffs won't re-render when a comment is made because they're subscribed to the overall thread previews data.
export function useGenerateThreadPreviews(
  threadIDs: number[],
  diffEntriesMap: Record<string, DiffEntry>,
): PendingCommentPreview[] {
  const threads = useThreads(threadIDs)
  const diffSummaries = useDiffSummaries()
  const pendingCommentPreviews: PendingCommentPreview[] = useMemo(() => {
    let threadIDsRemaining = threadIDs
    if (threads === undefined || diffSummaries === undefined) {
      return []
    }
    const buildingPendingCommentPreviews: PendingCommentPreview[] = []
    for (const diffEntryKey of Object.keys(diffEntriesMap)) {
      const diffEntry = diffEntriesMap[diffEntryKey]
      if (!diffEntry) continue
      let currentDiffLineIndex = 0
      const {path, diffLines} = diffEntry
      const diffSummary = diffSummaries[path]
      if (diffSummary) {
        const markersOnThisFile = findEntireFileMarkerByPosition(diffSummary, threads)
        if (markersOnThisFile.length > 0) {
          buildingPendingCommentPreviews.push(...markersOnThisFile)
          threadIDsRemaining = threadIDsRemaining.filter(
            threadID => !markersOnThisFile.some(marker => marker.threadId === threadID.toString()),
          )
        }

        diffLines.map(diffLine => {
          const markersInThisLine = findEntireMarkerByPosition(
            diffEntry.path,
            diffLine,
            diffSummary,
            threads,
            currentDiffLineIndex,
            diffLines,
          )
          currentDiffLineIndex++
          if (markersInThisLine !== undefined) {
            buildingPendingCommentPreviews.push(...markersInThisLine.pendingCommentsReturnArray)
            threadIDsRemaining = threadIDsRemaining.filter(
              threadID => !markersInThisLine.markersFoundInLine.includes(threadID),
            )
          }
        })
      }
    }
    return [
      ...buildingPendingCommentPreviews,
      ...buildPendingCommentReturnArrayWithNoContext(threadIDsRemaining, threads),
    ]
  }, [threadIDs, threads, diffSummaries, diffEntriesMap])

  return pendingCommentPreviews
}

function buildPendingCommentReturnArrayWithNoContext(
  threadIDsRemaining: number[],
  threads: Record<number, Thread>,
): PendingCommentPreview[] {
  const pendingCommentsReturnArray: PendingCommentPreview[] = []
  for (const threadID of threadIDsRemaining) {
    const thread = threads[threadID]
    if (!thread) continue

    const previousComments = thread.commentsData?.comments.slice(0, -1)
    const threadPreviewComments: CommentPreviews[] = []
    for (const comment of previousComments ?? []) {
      if (comment.author) {
        threadPreviewComments.push({
          author: {
            avatarUrl: comment.author.avatarUrl,
            login: comment.author.login,
          },
        })
      }
    }

    const pendingComments: PendingCommentPreview[] = (thread.commentsData?.comments ?? [])
      .filter(currentComment => currentComment.state === 'pending')
      .map(currentComment => ({
        bodyHTML: (currentComment.bodyHTML ?? '') as SafeHTMLString,
        threadId: thread.id,
        commentId: (currentComment.databaseId ?? 0).toString(),
        isOutdated: false,
        isResolved: false,
        path: '', // We couldn't find the comment in the diff, so we don't have a path
        subjectType: (currentComment.subjectType ?? 'LINE') as 'LINE' | 'FILE' | undefined,
        threadPreviewComments,
      }))

    pendingCommentsReturnArray.push(...pendingComments)
  }
  return pendingCommentsReturnArray
}

function findEntireMarkerByPosition(
  path: string,
  diffLine: DiffLine,
  diffEntryData: PullRequestFileTreeDiff,
  threads: Thread[],
  currentDiffLineIndex: number,
  fullDiffLineSet: DiffLine[],
): {pendingCommentsReturnArray: PendingCommentPreview[]; markersFoundInLine: number[]} {
  const diffLineKey = diffLine.type !== 'DELETION' ? `R${diffLine.right}` : `L${diffLine.left}`

  if (!diffEntryData || !diffEntryData.markersMap) {
    return {pendingCommentsReturnArray: [], markersFoundInLine: []}
  }

  // Array of marker positions
  const markerPositions = diffEntryData.markersMap[diffLineKey]
  const markersFoundInLine = []
  let pendingCommentsReturnArray: PendingCommentPreview[] = []

  if (markerPositions) {
    for (const marker of markerPositions.threads) {
      if (marker.id === undefined) continue
      const thread = threads.find(t => Number(t.id) === marker.id)
      if (!thread) continue
      const endLineNumber = parseInt(diffLineKey.slice(1)) || 0
      let startLineNumber = -1
      let startSide: DiffSide | undefined = undefined

      //grab the default number of context lines unless the thread has a start line
      let contextLinesToGrab = defaultContextLinesToGrab
      if (marker.start) {
        startSide = marker.start[0] === 'L' ? 'LEFT' : 'RIGHT'
        startLineNumber = parseInt(marker.start.slice(1)) || -1
      }
      if (startLineNumber !== -1) {
        contextLinesToGrab = endLineNumber - startLineNumber
      }
      if (contextLinesToGrab > currentDiffLineIndex) {
        //grab up to the top of the diff lines we have available
        contextLinesToGrab = currentDiffLineIndex + 1
      }
      const diffLineSubset = fullDiffLineSet.slice(
        currentDiffLineIndex + 1 - contextLinesToGrab,
        currentDiffLineIndex + 1,
      )
      for (let i = 0; i < diffLineSubset.length; i++) {
        if (diffLineSubset[i]?.type === 'HUNK') {
          //remove hunk lines from the context we send to the server
          diffLineSubset.splice(0, i + 1)
        }
      }
      const previousComments = thread.commentsData?.comments.slice(0, -1)
      const threadPreviewComments: CommentPreviews[] = []
      for (const comment of previousComments ?? []) {
        if (comment.author) {
          threadPreviewComments.push({
            author: {
              avatarUrl: comment.author.avatarUrl,
              login: comment.author.login,
            },
          })
        }
      }
      markersFoundInLine.push(marker.id)
      //building this way only works for non outdated comments
      //how do we want to handle outdated comments in this context? Do we just not display them?
      //or do we make the server call to get their information?
      if (thread.commentsData) {
        pendingCommentsReturnArray = pendingCommentsReturnArray.concat(
          thread.commentsData.comments
            .filter(currentComment => currentComment.state === 'pending')
            .map(currentComment => ({
              bodyHTML: (currentComment.bodyHTML ?? '') as SafeHTMLString,
              threadId: thread.id,
              commentId: (currentComment.databaseId ?? 0).toString(),
              isOutdated: false,
              isResolved: false, // pending comments can't be resolved
              line: endLineNumber,
              path,
              subject: {
                diffLines: diffLineSubset as StaticDiffLine[],
                endLine: endLineNumber,
                startDiffSide: startSide,
                startLine: startLineNumber === -1 ? null : startLineNumber,
              },
              subjectType: (currentComment.subjectType ?? 'LINE') as 'LINE' | 'FILE' | undefined,
              threadPreviewComments,
            })),
        )
      }
    }
    return {pendingCommentsReturnArray, markersFoundInLine}
  }
  return {pendingCommentsReturnArray: [], markersFoundInLine: []}
}

function findEntireFileMarkerByPosition(
  diffEntryData: PullRequestFileTreeDiff,
  threads: Thread[],
): PendingCommentPreview[] {
  if (!diffEntryData || !diffEntryData.markersMap) {
    return []
  }

  const markers = diffEntryData.markersMap['FILE']
  if (markers === undefined) {
    return []
  }

  const pendingCommentsReturnArray: PendingCommentPreview[] = []
  for (const marker of markers.threads) {
    if (marker.id === undefined) continue
    const thread = threads.find(t => Number(t.id) === marker.id)
    if (!thread) continue

    const previousComments = thread.commentsData?.comments.slice(0, -1)
    const threadPreviewComments: CommentPreviews[] = []
    for (const comment of previousComments ?? []) {
      if (comment.author) {
        threadPreviewComments.push({
          author: {
            avatarUrl: comment.author.avatarUrl,
            login: comment.author.login,
          },
        })
      }
    }

    const pendingComments: PendingCommentPreview[] = (thread.commentsData?.comments ?? [])
      .filter(currentComment => currentComment.state === 'pending')
      .map(currentComment => ({
        bodyHTML: (currentComment.bodyHTML ?? '') as SafeHTMLString,
        threadId: thread.id,
        commentId: (currentComment.databaseId ?? 0).toString(),
        isOutdated: false,
        isResolved: false, // pending comments can't be resolved
        path: diffEntryData.path,
        subjectType: 'FILE',
        threadPreviewComments,
      }))

    pendingCommentsReturnArray.push(...pendingComments)
  }

  return pendingCommentsReturnArray
}
